import React from "react";
import { FaAngleUp, FaAngleDown, FaArrowLeft } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import type { U2000GridStateHandlers } from "../../../reducer/formReducer/U2000GridStateHandlers";
import { U2000GridStateEnumPaginationDirection } from "../../../reducer/formReducer/U2000GridState";
import type { U2000FormStateHandlers } from "../../../reducer/formReducer/U2000FormStateHandlers";
import { U2000FormStateEnumState } from "../../../reducer/formReducer/U2000FormState";
import { u2000FormatIntegerCount } from "../../../tools/U2000NumberFormat";

// Typescript Props
interface U2000FormNavigationActionsProps {
  formStateHandlers: U2000FormStateHandlers<any>;
  gridStateHandlers: U2000GridStateHandlers<any>;
  onRecordChange?: (record: any) => void;
  onPageChange?: (page: number, direction: U2000GridStateEnumPaginationDirection) => void;
  handleGoBackFn?: () => void;
}

const U2000FormNavigationActions: React.FC<U2000FormNavigationActionsProps> = ({ formStateHandlers, gridStateHandlers, onRecordChange, onPageChange, handleGoBackFn }) => {
  //const { editMode } = formState;
  const totalRows = gridStateHandlers.getTotalRows();
  const currentIdx = gridStateHandlers.getCurrentIdx();
  const currentPage = gridStateHandlers.getCurrentPage();
  const pageSize = gridStateHandlers.getSize();
  const recordIdx = currentPage * pageSize - (pageSize - currentIdx) + 1;
  const isDisabled = formStateHandlers.getFormState() === U2000FormStateEnumState.EDIT;

  const { t, i18n } = useTranslation();
  const formattedRecordIdx = u2000FormatIntegerCount(recordIdx, i18n.language);
  const formattedTotalRows = u2000FormatIntegerCount(totalRows, i18n.language);

  const handlePrev = () => {
    if (currentIdx === 0 && gridStateHandlers.getCurrentPage() !== 1) {
      const newPage = gridStateHandlers.getCurrentPage() - 1;
      gridStateHandlers.setPage(newPage);
      if (onPageChange) {
        onPageChange(newPage, U2000GridStateEnumPaginationDirection.PREVIOUSPAGE);
      }
    } else {
      if (onRecordChange) {
        onRecordChange(gridStateHandlers.getData()[gridStateHandlers.previousRow()]);
      }
    }
  };

  const handleNext = () => {
    if (currentIdx === pageSize - 1 && currentPage * pageSize < totalRows) {
      const newPage = currentPage + 1;
      gridStateHandlers.setPage(newPage);
      if (onPageChange) {
        onPageChange(newPage, U2000GridStateEnumPaginationDirection.NEXTPAGE);
      }
    } else {
      if (onRecordChange) {
        onRecordChange(gridStateHandlers.getData()[gridStateHandlers.nextRow()]);
      }
    }
  };

  return (
    <nav className="flex items-center justify-center space-x-1 bg-neutral-50 rounded-xl  border border-neutral-200" role="navigation" aria-label="Navigation des enregistrements">
      {totalRows > 0 && (
        <>
          {/* Previous Button */}
          {recordIdx <= 1 || isDisabled ? (
            <div className="flex items-center justify-center w-10 h-10 text-neutral-400 cursor-not-allowed rounded-lg" aria-hidden="true">
              <FaAngleUp size={18} />
            </div>
          ) : (
            <button
              onClick={handlePrev}
              className="flex items-center justify-center w-10 h-10 text-neutral-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-200 focus:outline-hidden focus:ring-2 focus:ring-primary-500"
              aria-label={t("btnFormNavigationPrevious")}
              title={t("btnFormNavigationPrevious")}
            >
              <FaAngleUp size={18} />
            </button>
          )}

          {/* Record Counter */}
          <span
            className="min-w-[60px] text-center text-sm font-medium text-neutral-700 px-2"
            role="status"
            aria-live="polite"
            aria-label={`Enregistrement ${formattedRecordIdx} sur ${formattedTotalRows}`}
          >
            {formattedRecordIdx} / {formattedTotalRows}
          </span>

          {/* Next Button */}
          {recordIdx >= totalRows || isDisabled ? (
            <div className="flex items-center justify-center w-10 h-10 text-neutral-400 cursor-not-allowed rounded-lg" aria-hidden="true">
              <FaAngleDown size={18} />
            </div>
          ) : (
            <button
              onClick={handleNext}
              className="flex items-center justify-center w-10 h-10 text-neutral-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-200 focus:outline-hidden focus:ring-2 focus:ring-primary-500"
              aria-label={t("btnFormNavigationNext")}
              title={t("btnFormNavigationNext")}
            >
              <FaAngleDown size={18} />
            </button>
          )}
        </>
      )}

      {/* Separator */}
      {totalRows > 0 && handleGoBackFn && <div className="w-px h-6 bg-neutral-300 mx-1" aria-hidden="true" />}

      {/* Back to List Button */}
      {handleGoBackFn && (
        <button
          onClick={handleGoBackFn}
          className={`flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-200 focus:outline-hidden focus:ring-2 focus:ring-primary-500
            ${isDisabled ? "text-neutral-400 cursor-not-allowed" : "text-neutral-600 hover:text-primary-600 hover:bg-primary-50"}`}
          aria-label={t("btnFormNavigationReturnToGrid")}
          title={t("btnFormNavigationReturnToGrid")}
          disabled={isDisabled}
        >
          <FaArrowLeft size={18} />
        </button>
      )}
    </nav>
  );
};

export default U2000FormNavigationActions;
